import { useState } from "react";
import {
  Bath, BedDouble, Baby, Thermometer, ShieldCheck, Wifi,
  CookingPot, Home, TreePine, Car, Briefcase, Tv,
  ChevronDown, X,
} from "lucide-react";
import { Reveal } from "@/components/motion";
import Modal from "@/components/Modal";

const CATEGORY_ICONS = {
  Bagno: Bath,
  "Camera da letto e lavanderia": BedDouble,
  Famiglia: Baby,
  "Riscaldamento e climatizzazione": Thermometer,
  "Sicurezza domestica": ShieldCheck,
  "Internet e ufficio": Wifi,
  "Cucina e zona pranzo": CookingPot,
  "Caratteristiche dell'alloggio": Home,
  "All'aperto": TreePine,
  "Parcheggi e strutture": Car,
  Servizi: Briefcase,
  Intrattenimento: Tv,
};

const PREVIEW_CATEGORIES = 3;

const CategoryBlock = ({ cat, lang, ci, reveal }) => {
  const CatIcon = CATEGORY_ICONS[cat.categoria.it] || Home;
  const content = (
    <div>
      <p className="flex items-center gap-2.5 text-xs uppercase tracking-[0.15em] text-terracotta font-semibold" data-testid={`amenity-category-${ci}`}>
        <CatIcon className="h-4 w-4" />
        {cat.categoria[lang]}
      </p>
      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-4">
        {cat.voci.map((v) => (
          <li key={v.it} className="text-ink/80 pl-6">{v[lang]}</li>
        ))}
      </ul>
    </div>
  );
  return reveal ? (
    <Reveal key={cat.categoria.it} delay={ci * 0.05} y={16}>
      {content}
    </Reveal>
  ) : (
    <div key={cat.categoria.it}>{content}</div>
  );
};

const UnitAmenities = ({ unit, up, lang }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const categories = unit.amenityCategories;
  const preview = categories.slice(0, PREVIEW_CATEGORIES);
  const totalItems = categories.reduce((sum, c) => sum + c.voci.length, 0);
  const canShowMore = categories.length > PREVIEW_CATEGORIES || unit.unavailableAmenities.length > 0;

  return (
    <div className="md:col-span-7">
      <Reveal>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight">{up.amenitiesTitle}</h2>
      </Reveal>

      <div className="mt-10 space-y-8">
        {preview.map((cat, ci) => (
          <CategoryBlock key={cat.categoria.it} cat={cat} lang={lang} ci={ci} reveal />
        ))}
      </div>

      {canShowMore && (
        <button
          type="button"
          data-testid="unit-amenities-toggle"
          onClick={() => setModalOpen(true)}
          className="group mt-8 inline-flex items-center gap-3 rounded-full border border-ink/20 px-6 py-3 text-sm font-medium hover:border-terracotta hover:text-terracotta transition-colors duration-300"
        >
          {`${up.showAllAmenities} ${totalItems}`}
          <ChevronDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
        </button>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={up.amenitiesTitle} testId="unit-amenities-modal">
        <div className="space-y-8">
          {categories.map((cat, ci) => (
            <CategoryBlock key={cat.categoria.it} cat={cat} lang={lang} ci={ci} />
          ))}
          {unit.unavailableAmenities.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-stone font-semibold">{up.notAvailableTitle}</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-4">
                {unit.unavailableAmenities.map((v) => (
                  <li key={v.it} className="flex items-center gap-3 text-stone/70">
                    <X className="h-4 w-4 shrink-0" />
                    <span className="line-through">{v[lang]}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default UnitAmenities;
